/**
 * The SVG sketch canvas: rendering + pointer interaction.
 *
 * Structure mirrors the prototype: a single `<g class="viewport">` carries the
 * pan/zoom transform, and everything inside it is in MODEL space. Screen
 * pointer positions are converted to model space via the viewport's own CTM, so
 * a stylus maps to the exact point under its tip at any zoom.
 *
 * Three modes: 'draw' (freehand strokes), 'select' (pick/inspect a wall), and
 * 'pan'. Two-finger pinch zooms in any mode. The canvas emits high-level events
 * ('select', 'strokeEnd', 'viewChange') and otherwise leaves state ownership to
 * the caller — it renders what it is given and reports what the user did.
 *
 * Ported and restructured from
 * reference/bluetooth-room-sketch-prototype/index.html.
 */
import {
  MAX_SCALE,
  MIN_SCALE,
  fitPoints,
  identityView,
  panBy,
  toModel,
  toScreen,
  transformString,
  zoomAt,
} from './view.js';
import { assignLabelLanes, wallBands } from '../geometry/index.js';
import { ceilingOpeningGeometry } from '../geometry/ceiling-opening.js';
import { floatTextInput } from './text-input.js';
import { displayRunScale } from './visual-snap-closure.js';

const SVGNS = 'http://www.w3.org/2000/svg';

/** Render fallback depth (units) for an obstruction with no measured depth,
 *  so it is still visible on the plan. The owner's default: 1/2". */
const DEFAULT_OBSTRUCTION_DEPTH_UNITS = 0.5;

/**
 * How far INTO THE ROOM a fixture's label sits from its inner face, and how far
 * each extra LANE steps out beyond that — both in DRAWING px, not room units.
 *
 * The distinction is the whole point (owner, 2026-08-05: labels overlapping on
 * a large room). These offsets have to stay in step with the LABEL, and a label
 * is a fixed ~11px tall however big the room is. Stated in room units they were
 * multiplied by pxPerUnit, so on a big room — where an inch is worth few pixels
 * — the lanes closed up under the type while the text stayed the same size. A
 * 5" step is 127px in a 62" closet and 16px in a 40' room; the second overlaps.
 *
 * In drawing px both the offsets and the text ride the same viewport transform,
 * so they scale together at every zoom and on the fitted sheet. The lane step
 * clears the WIDEST label the codes produce ("SF1", "O10") because a wall whose
 * normal runs horizontally stacks its lanes sideways.
 */
const OBSTRUCTION_LABEL_GAP_PX = 14;
const OBSTRUCTION_LABEL_LANE_PX = 34;

function el(tag, attrs = {}) {
  const node = document.createElementNS(SVGNS, tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  return node;
}

const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const pointsAttr = (pts) => pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');

/**
 * Quarter-circle plan geometry for a single swing door. Hinge handing is
 * relative to the wall's canonical start/end direction; in/out selects which
 * physical wall face receives the open leaf. Kept pure so the editable canvas
 * and measurement-sheet renderer cannot disagree about the arc.
 */
export function doorSwingGeometry({
  p0, p1, along, inward, widthPx, clearWidthPx = widthPx,
  hingeInsetPx = 0, leafAllowancePx = 0,
  hingeSide = 'left', swingDirection = 'in',
}) {
  if (!p0 || !p1 || !along || !inward || !(widthPx > 0) || !(clearWidthPx > 0)) return null;
  const left = hingeSide !== 'right';
  const inset = Math.max(0, Math.min(Number(hingeInsetPx) || 0, widthPx / 2));
  const leafWidth = clearWidthPx + Math.max(0, Number(leafAllowancePx) || 0);
  const edge = left ? p0 : p1;
  const hinge = {
    x: edge.x + along.x * inset * (left ? 1 : -1),
    y: edge.y + along.y * inset * (left ? 1 : -1),
  };
  const closedVector = left
    ? { x: along.x * leafWidth, y: along.y * leafWidth }
    : { x: -along.x * leafWidth, y: -along.y * leafWidth };
  const side = swingDirection === 'out' ? -1 : 1;
  const openVector = { x: inward.x * leafWidth * side, y: inward.y * leafWidth * side };
  const closedEnd = { x: hinge.x + closedVector.x, y: hinge.y + closedVector.y };
  const openEnd = { x: hinge.x + openVector.x, y: hinge.y + openVector.y };
  const cross = closedVector.x * openVector.y - closedVector.y * openVector.x;
  return { hinge, closedEnd, openEnd, radius: leafWidth, sweep: cross >= 0 ? 1 : 0 };
}

/**
 * Folded bifold stacks always park OUTSIDE the measured room. A single stack
 * occupies the chosen end; a double occupies both ends. Returned line segments
 * are deliberately simple so editable and MS plans render the same clearance.
 */
export function bifoldDoorGeometry({
  p0, p1, inward, projectionPx,
  kind = 'bifold_single', foldSide = 'left',
}) {
  if (!p0 || !p1 || !inward || !(projectionPx > 0)
    || !['bifold_single', 'bifold_double'].includes(kind)) return [];
  const outside = { x: -inward.x * projectionPx, y: -inward.y * projectionPx };
  const segment = (point, side) => ({
    side,
    from: point,
    to: { x: point.x + outside.x, y: point.y + outside.y },
  });
  if (kind === 'bifold_double') return [segment(p0, 'left'), segment(p1, 'right')];
  return [foldSide === 'right' ? segment(p1, 'right') : segment(p0, 'left')];
}

/**
 * @param {SVGSVGElement} svg the root <svg>
 * @param {object} handlers
 * @param {(id:string|null)=>void} handlers.onSelectWall
 * @param {(points:{x:number,y:number}[])=>void} handlers.onStrokeEnd
 */
export function createCanvas(svg, handlers = {}) {
  const viewport = el('g', { class: 'viewport' });
  // To-scale grid, behind everything. Its pattern is defined once; a big
  // background rect shows it, transformed with the viewport so it pans/zooms.
  const gridLayer = el('g', { class: 'grid-layer' });
  const rawLayer = el('g', { class: 'raw-layer' });
  // Wall thickness bands + on-plan obstructions sit BELOW the wall lines and
  // labels, so the drawn line (the authoritative front face) and the number
  // chips always read on top of the fill.
  const thicknessLayer = el('g', { class: 'thickness-layer' });
  const wallLayer = el('g', { class: 'wall-layer' });
  const ceilingOpeningLayer = el('g', { class: 'ceiling-opening-layer' });
  const labelLayer = el('g', { class: 'label-layer' });
  // Freehand annotations sit ON TOP of everything — they are the design
  // overlay drawn over the locked room (ADR 0023).
  const annotationLayer = el('g', { class: 'annotation-layer' });
  const measureLayer = el('g', { class: 'measure-layer' });
  const activeStroke = el('polyline', { class: 'active-stroke', points: '' });

  /**
   * Styles the live stroke as the PEN, so a sketch is drawn in its real colour
   * and thickness rather than changing after the fact (owner, 2026-07-29).
   *
   * These must be INLINE styles. `.active-stroke` sets stroke and stroke-width
   * in the stylesheet, and in SVG a stylesheet rule beats a presentation
   * attribute — so `setAttribute('stroke', …)` was silently losing to the CSS
   * and every stroke previewed in the accent blue. Inline style wins, which is
   * the only reliable way to override a class rule here.
   *
   * `vector-effect: none` matches the COMMITTED stroke, which scales with zoom;
   * the wall guide keeps the non-scaling default. Otherwise the preview would be
   * a different thickness from the line you end up with.
   */
  const penPreviewStyle = () => {
    activeStroke.style.stroke = annotationStyle.color;
    activeStroke.style.strokeWidth = String(annotationStyle.width);
    activeStroke.style.vectorEffect = 'none';
  };
  /** Back to the stylesheet's guide-line look for drawing walls. */
  const wallGuideStyle = () => {
    activeStroke.style.stroke = '';
    activeStroke.style.strokeWidth = '';
    activeStroke.style.vectorEffect = '';
  };
  viewport.append(gridLayer, rawLayer, thicknessLayer, wallLayer, ceilingOpeningLayer, labelLayer, annotationLayer, measureLayer, activeStroke);
  // Wall-number labels live OUTSIDE the viewport transform, so they stay a
  // constant, legible size at any zoom instead of shrinking away. Positioned by
  // projecting each wall's outside-midpoint to screen (see renderLabels).
  const labelOverlay = el('g', { class: 'label-overlay' });
  svg.append(viewport, labelOverlay);

  let view = identityView();
  let mode = 'draw';
  let walls = [];
  let selectedWallId = null;
  let annotations = []; // rendered strokes, kept for eraser hit-testing
  let annotationStyle = { color: '#e5484d', width: 2 };
  let annotationStraight = false; // straight-line tool vs freehand
  let labelAnchors = []; // wall-label positions in MODEL coords, projected each frame
  let ceilingOpeningLabelAnchors = []; // constant-size attic labels, also projected

  const activePointers = new Map();
  let drawing = null; // { points, annotation? }
  let panDrag = null; // { last }
  let pinch = null;
  let textDrag = null; // repositioning a text note: { id, node, orig, grab, last }
  let labelDrag = null; // sliding a wall label along its wall: { id, node, ctx, last }
  let sectionDrag = null; // sliding a section caption to REORDER it: { id, node, ctx, slot }
  let measures = []; // completed tape measurements: [{ a, b }] — stay until erased
  let activeMeasure = null; // the one being dragged right now
  let lastPxPerUnit = 0; // scale captured at render, for the measure label
  let lastFmt = null; // formatter captured at render, ditto
  let erasing = false;

  const viewBox = () => {
    const r = svg.viewBox.baseVal;
    return { w: r.width || svg.clientWidth || 1200, h: r.height || svg.clientHeight || 800 };
  };

  function applyView() {
    viewport.setAttribute('transform', transformString(view));
    renderLabels(); // labels live outside the transform — reposition on pan/zoom
    handlers.onViewChange?.(view);
  }

  /**
   * Draw the wall-number chips in the (untransformed) overlay, projecting each
   * anchor's MODEL position to screen so the chip stays a constant, legible
   * size at any zoom. Chips off the visible area are skipped.
   */
  function renderLabels() {
    labelOverlay.replaceChildren();
    const { w, h } = viewBox();
    for (const a of labelAnchors) {
      const p = toScreen(view, a);
      if (p.x < -80 || p.y < -30 || p.x > w + 80 || p.y > h + 30) continue; // off-screen
      const group = el('g', { class: `wall-label${a.selected ? ' wall-label--selected' : ''}${a.captureArmed ? ' wall-label--capture-armed' : ''}${a.elevHidden ? ' wall-label--elev-hidden' : ''}${a.isAuto ? ' wall-label--auto' : ''}`, transform: `translate(${p.x.toFixed(1)},${p.y.toFixed(1)})` });
      const rect = el('rect', { y: -19, height: 38, rx: 9, class: 'wall-label__bg' });
      const number = el('text', { class: 'wall-label__number', 'text-anchor': 'middle', y: -4 });
      number.textContent = a.number;
      const len = el('text', { class: 'wall-label__length', 'text-anchor': 'middle', y: 13 });
      len.textContent = a.length;
      group.append(rect, number, len);
      group.addEventListener('pointerdown', (evt) => {
        if (mode !== 'select' || !a.wallId) return;
        evt.stopPropagation();
        handlers.onSelectWall?.(a.wallId);
      });
      // An auto chip is tap-to-confirm: lock in the solver's length as measured
      // (Select mode only, so it never fires mid-draw). A plain click, not
      // pointerdown, so a pan/zoom gesture over the chip doesn't confirm it.
      if (a.isAuto && a.wallId) {
        group.style.cursor = 'pointer';
        const t = el('title'); t.textContent = 'Tap to confirm this auto length';
        group.append(t);
        group.addEventListener('click', (evt) => {
          if (mode !== 'select') return;
          evt.stopPropagation();
          handlers.onConfirmWallLength?.(a.wallId);
        });
      }
      labelOverlay.append(group);
      // Size the chip to its content so it is never wider than it needs to be.
      let contentW = 34;
      try { contentW = Math.max(number.getBBox().width, len.getBBox().width); } catch { /* not measurable */ }
      const width = Math.min(Math.max(contentW + 14, 34), 130);
      rect.setAttribute('x', (-width / 2).toFixed(1));
      rect.setAttribute('width', width.toFixed(1));
    }
    for (const a of ceilingOpeningLabelAnchors) {
      const p = toScreen(view, a);
      if (p.x < -120 || p.y < -30 || p.x > w + 120 || p.y > h + 30) continue;
      const label = el('text', {
        'text-anchor': 'middle',
        class: 'ceiling-opening__label',
      });
      const title = el('tspan', { x: p.x.toFixed(1), y: (p.y - 9).toFixed(1) });
      title.textContent = a.title ?? 'Attic';
      const refs = el('tspan', { x: p.x.toFixed(1), y: (p.y + 9).toFixed(1), class: 'ceiling-opening__label-ref' });
      refs.textContent = a.refs;
      label.append(title, refs);
      labelOverlay.append(label);
    }
  }

  /** client coords -> model space, honoring the current pan/zoom. */
  function toModelPoint(clientX, clientY) {
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const ctm = viewport.getScreenCTM();
    if (ctm) {
      const m = pt.matrixTransform(ctm.inverse());
      return { x: m.x, y: m.y };
    }
    // Fallback for headless/no-layout environments (tests, SSR).
    const box = svg.getBoundingClientRect?.() ?? { left: 0, top: 0 };
    return toModel(view, { x: clientX - box.left, y: clientY - box.top });
  }

  /** client coords -> the outer SVG's own space, independent of zoom. */
  function toSvgPoint(clientX, clientY) {
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const ctm = svg.getScreenCTM();
    if (ctm) {
      const m = pt.matrixTransform(ctm.inverse());
      return { x: m.x, y: m.y };
    }
    const box = svg.getBoundingClientRect?.() ?? { left: 0, top: 0 };
    return { x: clientX - box.left, y: clientY - box.top };
  }

  // ---- rendering -----------------------------------------------------------

  function renderRaw(strokes, current) {
    rawLayer.replaceChildren();
    for (const stroke of strokes) {
      rawLayer.append(el('polyline', { class: 'raw-stroke', points: pointsAttr(stroke) }));
    }
    activeStroke.setAttribute('points', current ? pointsAttr(current) : '');
  }

  /**
   * @param {object} [opts]
   * @param {(wall)=>number} [opts.thicknessFor] wall thickness in UNITS (not px)
   * @param {(wallId:string)=>object[]} [opts.obstructionsFor] fixtures on a wall
   * @param {number} [opts.pxPerUnit] units→px scale for thickness/obstructions
   */
  function renderWalls(nextWalls, selection, labelFor, opts = {}) {
    walls = nextWalls;
    selectedWallId = selection;
    lastPxPerUnit = opts.pxPerUnit > 0 ? opts.pxPerUnit : 0; // for the tape measure
    lastFmt = typeof opts.formatLength === 'function' ? opts.formatLength : lastFmt;
    thicknessLayer.replaceChildren();
    wallLayer.replaceChildren();
    ceilingOpeningLayer.replaceChildren();
    labelLayer.replaceChildren();
    renderGrid(opts);

    const selectWall = (id) => (evt) => {
      if (mode !== 'select') return;
      evt.stopPropagation();
      handlers.onSelectWall?.(id);
    };

    // A wall IS its thickness band — a single solid 4.5" shape, not a line with
    // a band beside it (owner, 2026-07-24). Compute all bands up front (miters
    // need the neighbours), then each wall draws its band when we have a scale,
    // or falls back to a plain line before the room is solved / when signed
    // out. thicknessFor returns UNITS; the band geometry works in px.
    const { thicknessFor, pxPerUnit, partitionIds } = opts;
    const bandById = new Map();
    if (pxPerUnit > 0 && typeof thicknessFor === 'function') {
      // Partition footprints are `opening` segments the boundary must stay solid
      // through (ADR 0032), so they get a band like a wall does.
      for (const band of wallBands(nextWalls, { thicknessFor: (seg) => (thicknessFor(seg) || 0) * pxPerUnit, partitionIds })) {
        bandById.set(band.id, band);
      }
    }

    // Room centroid (from wall/opening starts), so labels can be pushed to the
    // side AWAY from the interior.
    const starts = nextWalls.map((s) => s.start);
    const centroid = starts.length
      ? starts.reduce((a, p) => ({ x: a.x + p.x / starts.length, y: a.y + p.y / starts.length }), { x: 0, y: 0 })
      : { x: 0, y: 0 };
    labelAnchors = [];
    ceilingOpeningLabelAnchors = [];

    renderPlanObstructions(nextWalls, opts);
    renderPlanSections(nextWalls, opts);
    renderPartitions(nextWalls, selection, selectWall, opts);
    renderCeilingOpenings(nextWalls, opts);

    for (const w of nextWalls) {
      const isOpening = (w.segmentKind ?? 'wall') === 'opening';
      const isPartition = !!(partitionIds && partitionIds.has(w.id));
      const selected = w.id === selection;
      const captureArmed = w.id === opts.armedMeasurementWallId;
      // A wall the designer excluded from the front elevation is drawn greyed on
      // the plan, so it is easy to see WHICH walls the elevation is showing.
      const elevHidden = !isOpening && w.includeInElevation === false;
      const band = bandById.get(w.id);

      // A real wall OR a partition footprint draws its solid band; a door
      // opening stays a dashed void.
      if (band && (!isOpening || isPartition)) {
        const poly = el('polygon', {
          points: pointsAttr(band.points),
          class: `wall-band${selected ? ' wall-band--selected' : ''}${captureArmed ? ' wall-band--capture-armed' : ''}${elevHidden ? ' wall-band--elev-hidden' : ''}`,
          'data-wall-id': w.id,
        });
        poly.addEventListener('pointerdown', selectWall(w.id));
        wallLayer.append(poly);
      } else {
        // Fallback / openings: the plain line (openings stay a dashed void).
        const line = el('line', {
          x1: w.start.x, y1: w.start.y, x2: w.end.x, y2: w.end.y,
          class: `wall${isOpening ? ' wall--opening' : ''}${selected ? ' wall--selected' : ''}${captureArmed ? ' wall--capture-armed' : ''}${elevHidden ? ' wall--elev-hidden' : ''}`,
          'data-wall-id': w.id,
        });
        line.addEventListener('pointerdown', selectWall(w.id));
        wallLayer.append(line);
      }

      // Openings carry no number, so they get no label chip.
      if (isOpening) continue;

      // Record the label's OUTSIDE-midpoint in model coords; renderLabels()
      // projects it and draws a constant-size chip just outside the wall.
      const mid = { x: (w.start.x + w.end.x) / 2, y: (w.start.y + w.end.y) / 2 };
      const dlen = Math.hypot(w.end.x - w.start.x, w.end.y - w.start.y) || 1;
      let out = { x: (w.end.y - w.start.y) / dlen, y: -(w.end.x - w.start.x) / dlen };
      if ((centroid.x - mid.x) * out.x + (centroid.y - mid.y) * out.y > 0) out = { x: -out.x, y: -out.y }; // face AWAY from interior
      const thickPx = (pxPerUnit > 0 && typeof thicknessFor === 'function' ? (thicknessFor(w) || 0) * pxPerUnit : 8);
      const gap = thickPx + (pxPerUnit > 0 ? 6 * pxPerUnit : 24); // wall band + ~6" clear
      // A measured wall shows its value; an unmeasured one shows the solver's
      // length in amber (auto) and is tap-to-confirm. Falls back to a dash only
      // before the room is solved (no auto length available yet).
      const measured = labelFor ? labelFor(w) : null;
      const auto = measured == null && typeof opts.autoLengthFor === 'function' ? opts.autoLengthFor(w) : null;
      labelAnchors.push({
        x: mid.x + out.x * gap,
        y: mid.y + out.y * gap,
        number: `W${w.wallNumber ?? '?'}`,
        length: measured ?? auto ?? '—',
        isAuto: measured == null && auto != null,
        wallId: w.id,
        selected,
        captureArmed,
        elevHidden,
      });
    }
    renderWallLabels(nextWalls, selectWall, opts);
    renderLabels();
  }

  /** Ceiling-hosted attic access: a 2-D plan object, never a perimeter segment. */
  function renderCeilingOpenings(chain, {
    ceilingOpenings = [], pxPerUnit, ladderCeilingInsetUnits, ladderSideClearanceUnits,
  } = {}) {
    if (!Array.isArray(ceilingOpenings) || !ceilingOpenings.length || !(pxPerUnit > 0) || !chain.length) return;
    const numbers = new Map(chain.map((wall) => [wall.id, wall.wallNumber]));
    for (const opening of ceilingOpenings) {
      const geometry = ceilingOpeningGeometry(chain, opening, { pxPerUnit, ladderCeilingInsetUnits, ladderSideClearanceUnits });
      if (!geometry) continue;
      const group = el('g', { class: `ceiling-opening ceiling-opening--${opening.kind}`, 'data-ceiling-opening-id': opening.id });
      if (['ceiling_fan', 'ceiling_light', 'ceiling_sprinkler'].includes(opening.kind)) {
        const angle = (Math.atan2(geometry.widthAxis.y, geometry.widthAxis.x) * 180) / Math.PI;
        const rx = opening.widthUnits * pxPerUnit / 2;
        const ry = opening.lengthUnits * pxPerUnit / 2;
        const fixture = el('g', {
          class: `ceiling-fixture ceiling-fixture--${({ ceiling_fan: 'fan', ceiling_light: 'light', ceiling_sprinkler: 'sprinkler' })[opening.kind]}`,
          transform: `translate(${geometry.center.x.toFixed(1)},${geometry.center.y.toFixed(1)}) rotate(${angle.toFixed(1)})`,
        });
        if (opening.kind === 'ceiling_fan') {
          fixture.append(el('circle', { cx: 0, cy: 0, r: rx, class: 'ceiling-fixture__outline' }));
          const hub = Math.max(2, Math.min(rx, ry) * 0.12);
          fixture.append(el('circle', { cx: 0, cy: 0, r: hub, class: 'ceiling-fixture__hub' }));
          for (const rotation of [0, 90, 180, 270]) {
            fixture.append(el('line', {
              x1: 0, y1: 0, x2: rx * 0.82, y2: 0,
              transform: `rotate(${rotation})`, class: 'ceiling-fixture__blade',
            }));
          }
        } else {
          fixture.append(el('ellipse', { cx: 0, cy: 0, rx, ry, class: 'ceiling-fixture__outline' }));
          fixture.append(el('line', { x1: -rx * 0.7, y1: 0, x2: rx * 0.7, y2: 0, class: 'ceiling-fixture__cross' }));
          fixture.append(el('line', { x1: 0, y1: -ry * 0.7, x2: 0, y2: ry * 0.7, class: 'ceiling-fixture__cross' }));
        }
        group.append(fixture);
      } else {
        group.append(el('polygon', { points: pointsAttr(geometry.points), class: 'ceiling-opening__shape' }));
      }
      if (geometry.ladder?.footprint && opening.showLadderExtended !== false) {
        group.append(el('polygon', {
          points: pointsAttr(geometry.ladder.footprint),
          class: 'ceiling-opening__ladder-footprint',
        }));
      }
      const refs = (opening.referenceWallIds ?? []).map((id) => `W${numbers.get(id) ?? '?'}`).join('/');
      ceilingOpeningLabelAnchors.push({
        ...geometry.center,
        refs,
        title: ({ ceiling_fan: 'Fan', ceiling_light: 'Light', ceiling_sprinkler: 'Sprinkler' })[opening.kind] ?? 'Attic',
      });
      ceilingOpeningLayer.append(group);
    }
  }

  /**
   * Standard section-type labels (ADR 0034) along each wall, rotated to the wall
   * and kept UPRIGHT (never upside-down — 0° and -180° walls both read normally).
   * Sits just inside the wall face. A tap drags it ALONG the wall; right-click
   * opens its menu (copy/delete).
   */
  function renderWallLabels(chain, selectWall, opts = {}) {
    const { wallLabelsFor, pxPerUnit } = opts;
    if (typeof wallLabelsFor !== 'function' || !pxPerUnit || pxPerUnit <= 0) return;
    const inward = inwardNormalFn(chain);
    for (const w of chain) {
      if ((w.segmentKind ?? 'wall') !== 'wall') continue;
      const labels = wallLabelsFor(w.id) ?? [];
      if (!labels.length) continue;
      const dx = w.end.x - w.start.x;
      const dy = w.end.y - w.start.y;
      const len = Math.hypot(dx, dy) || 1;
      const d = { x: dx / len, y: dy / len };
      const ni = inward(w);
      // Rotate to the wall, kept upright (never upside down). A vertical wall is
      // pinned by its dy sign — down reads +90, up reads -90 — so opposing walls
      // read opposite ways instead of both the same (owner: the -90° wall read
      // like the +90° one). Deciding verticals by sign, not by normalising the
      // raw angle, avoids floating-point noise flipping an exact -90° to +90°.
      let rot;
      if (Math.abs(dx) <= Math.abs(dy) * 1e-3) {
        rot = dy >= 0 ? 90 : -90;
      } else {
        rot = (Math.atan2(dy, dx) * 180) / Math.PI;
        if (rot > 90) rot -= 180;
        if (rot < -90) rot += 180;
      }
      const gap = pxPerUnit * 6; // ~6" inside the wall face — ACROSS the wall, unscaled
      // D98: a designer's wall label is pinned at a stated offset ALONG the
      // wall, exactly like a section's start, so it takes the same snap-close
      // factor (RS-1/ADR 0090). Before this it did not, and RS-1 made that
      // visible: sections moved to the drawn wall and labels stayed put, so a
      // label pinned to something drifted off it. They used to be wrong
      // together, and therefore aligned.
      const alongPxPerUnit = pxPerUnit * alongWallScale(w, opts);
      const dragCtx = { start: { x: w.start.x, y: w.start.y }, d, len, ni, gap, rot, pxPerRunUnit: alongPxPerUnit };
      for (const label of labels) {
        const alongPx = label.offsetAlongWall * alongPxPerUnit;
        const px = w.start.x + d.x * alongPx + ni.x * gap;
        const py = w.start.y + d.y * alongPx + ni.y * gap;
        const t = el('text', {
          transform: `translate(${px.toFixed(1)},${py.toFixed(1)}) rotate(${rot.toFixed(1)})`,
          class: 'wall-label-note',
          'text-anchor': 'middle',
          'data-wall-label-id': label.id,
        });
        // Real-inch sizing on the plan (inline style outranks the class's 20px,
        // which is for the elevation's normalized space) — same scale fix as
        // the section captions: fixed pixels turn huge on a small-drawn room.
        t.style.fontSize = `${(5 * pxPerUnit).toFixed(2)}px`;
        t.textContent = label.text;
        t.addEventListener('pointerdown', (evt) => beginLabelDrag(evt, label.id, dragCtx));
        t.addEventListener('contextmenu', (evt) => { evt.preventDefault(); handlers.onLabelMenu?.(label.id, evt.clientX, evt.clientY); });
        wallLayer.append(t);
      }
    }
  }

  /** Start dragging a wall label ALONG its wall (Select mode only). */
  function beginLabelDrag(evt, id, ctx) {
    if (mode !== 'select') return;
    evt.stopPropagation();
    labelDrag = { id, node: evt.currentTarget, ctx, last: null };
    svg.setPointerCapture(evt.pointerId);
  }

  /**
   * Draws obstructions on the plan — each protruding INWARD from its wall's
   * front face into the room by its depth. To scale via pxPerUnit. Wall bands
   * themselves are drawn in the main loop (a wall IS its band); this layer sits
   * below the wall shapes. See geometry/thickness.js and ADR 0020.
   */
  /**
   * A to-scale reference grid behind the plan (ADR 0024). An SVG pattern tiled
   * at the grid spacing (default 6") gives an effectively-infinite grid for one
   * cheap rect; both sit in the viewport, so the grid pans and zooms with the
   * drawing. Hidden when off, or before a scale exists, or when a cell would be
   * too small to read.
   */
  function renderGrid({ gridVisible, gridSpacingUnits = 6, pxPerUnit } = {}) {
    gridLayer.replaceChildren();
    if (!gridVisible || !pxPerUnit || pxPerUnit <= 0) return;
    const tile = gridSpacingUnits * pxPerUnit;
    if (tile < 3) return; // too dense on screen to help

    const defs = el('defs');
    const pattern = el('pattern', { id: 'plan-grid', width: tile, height: tile, patternUnits: 'userSpaceOnUse' });
    pattern.append(el('path', { d: `M ${tile} 0 L 0 0 0 ${tile}`, class: 'grid-line' }));
    defs.append(pattern);
    const R = 40000; // large enough to cover any pan at this app's scales
    gridLayer.append(defs, el('rect', { x: -R, y: -R, width: 2 * R, height: 2 * R, fill: 'url(#plan-grid)', class: 'grid-rect' }));
  }

  /**
   * A function giving the INTERIOR-facing unit normal of any segment in the
   * loop, from the loop's winding — matching how wall thickness picks its
   * outward side (thickness.js), so anything drawn inward (depth lines,
   * obstructions, partition spurs) always lands on the room side. Correct for
   * concave plans where a centroid test would flip (ADR 0032).
   */
  function inwardNormalFn(chain) {
    let sum = 0;
    for (let i = 0; i < chain.length; i++) {
      const a = chain[i].start;
      const b = chain[(i + 1) % chain.length].start;
      sum += a.x * b.y - b.x * a.y;
    }
    const winding = sum >= 0 ? 1 : -1;
    return (seg) => {
      const dx = seg.end.x - seg.start.x;
      const dy = seg.end.y - seg.start.y;
      const len = Math.hypot(dx, dy) || 1;
      // Outward (thickness) is { d.y·w, -d.x·w }; interior is its negation.
      return { x: (-dy / len) * winding, y: (dx / len) * winding };
    };
  }

  /**
   * The along-wall factor for one wall of the chain being drawn (RS-1).
   *
   * The chain handed to renderWalls is DISPLAY geometry: snap closure may have
   * moved a wall's endpoints so the room closes, leaving it drawn at a length
   * its measurement never claimed. `statedLengthUnitsFor` supplies the
   * measurement, resolved from the CANONICAL walls — deriving it from the
   * display wall's own endpoints would just recover the drawn length and
   * silently yield 1.
   */
  function alongWallScale(wall, opts) {
    const stated = typeof opts.statedLengthUnitsFor === 'function' ? opts.statedLengthUnitsFor(wall.id) : null;
    if (!Number.isFinite(stated)) return 1;
    return displayRunScale(distance(wall.start, wall.end), stated, opts.pxPerUnit);
  }

  function renderPlanObstructions(chain, opts = {}) {
    const { obstructionsFor, helperLinesFor, pxPerUnit } = opts;
    if (!pxPerUnit || pxPerUnit <= 0) return;

    // Inward normal per wall = the unit normal pointing toward the room's
    // interior, derived from the LOOP WINDING — the same source wall thickness
    // uses to pick its OUTWARD side. A centroid test (the old way) flips to the
    // wrong side on a concave plan (e.g. a peninsula/back-to-back walls), which
    // put depth lines off the back of a wall; winding is right for every edge of
    // a simple loop, convex or concave (owner report, ADR 0032).
    const inward = inwardNormalFn(chain);

    // Depth helper lines: a thin line offset INWARD from each wall's inside
    // face by its distance, spanning the wall (ADR 0024).
    if (typeof helperLinesFor === 'function') {
      for (const w of chain) {
        if ((w.segmentKind ?? 'wall') !== 'wall') continue;
        const lines = helperLinesFor(w.id) ?? [];
        if (!lines.length) continue;
        const ni = inward(w);
        for (const h of lines) {
          const o = h.offsetUnits * pxPerUnit;
          thicknessLayer.append(el('line', {
            x1: w.start.x + ni.x * o, y1: w.start.y + ni.y * o,
            x2: w.end.x + ni.x * o, y2: w.end.y + ni.y * o,
            class: 'plan-depth-line', 'data-helper-id': h.id,
          }));
        }
      }
    }

    if (typeof obstructionsFor !== 'function') return;

    for (const w of chain) {
      if ((w.segmentKind ?? 'wall') !== 'wall') continue;
      const items = obstructionsFor(w.id) ?? [];
      if (!items.length) continue;

      const dx = w.end.x - w.start.x;
      const dy = w.end.y - w.start.y;
      const wallLen = Math.hypot(dx, dy) || 1;
      const d = { x: dx / wallLen, y: dy / wallLen };            // along-wall unit
      const ni = inward(w);                                       // interior-facing normal
      // Along the wall, a room unit is worth this much (RS-1); across it — the
      // depth direction — an unscaled pxPerUnit still applies.
      const alongPxPerUnit = pxPerUnit * alongWallScale(w, opts);
      // Fixtures stacked at one position on the wall share a footprint here,
      // so their labels take different lanes out into the room.
      const lanes = assignLabelLanes(items);

      for (const item of items) {
        const alongPx = item.offsetAlongWall * alongPxPerUnit;
        const widthPx = item.widthUnits * alongPxPerUnit;
        const depthUnits = Number.isFinite(item.depthUnits) ? item.depthUnits : DEFAULT_OBSTRUCTION_DEPTH_UNITS;
        const depthPx = depthUnits * pxPerUnit;
        // The near face sits `faceOffsetUnits` inward from the wall face (signed:
        // negative recesses it into the wall); the item then extends by its
        // depth from there (ADR 0025).
        const facePx = (Number.isFinite(item.faceOffsetUnits) ? item.faceOffsetUnits : 0) * pxPerUnit;
        const nearAlong = { x: w.start.x + d.x * alongPx + ni.x * facePx, y: w.start.y + d.y * alongPx + ni.y * facePx };
        const p0 = nearAlong;
        const p1 = { x: p0.x + d.x * widthPx, y: p0.y + d.y * widthPx };
        const p2 = { x: p1.x + ni.x * depthPx, y: p1.y + ni.y * depthPx };
        const p3 = { x: p0.x + ni.x * depthPx, y: p0.y + ni.y * depthPx };
        // Where a label for this fixture belongs: just INSIDE the room, off its
        // inner face (owner, 2026-08-05 — the sheet's IDs sat half on the wall
        // band, black on black). p2–p3 is the inner edge and `ni` points into
        // the room, so this works at any wall angle, which "above it on screen"
        // does not. The gap is in UNITS, so it scales with the drawing exactly
        // as the text does.
        const inner = { x: (p2.x + p3.x) / 2, y: (p2.y + p3.y) / 2 };
        const labelGapPx = OBSTRUCTION_LABEL_GAP_PX
          + (lanes.get(item.id) ?? 0) * OBSTRUCTION_LABEL_LANE_PX;
        thicknessLayer.append(el('polygon', {
          // Reuse the elevation's base + type classes so colours match exactly
          // (the base gives windows their stroke; plan-obstruction tunes the
          // rest and is pointer-events:none, so the base's grab cursor is moot).
          points: pointsAttr([p0, p1, p2, p3]),
          class: `elev-obstruction elev-obstruction--${item.type} plan-obstruction${item.type === 'wall_opening' ? ` plan-opening--${item.opening?.kind ?? 'swing'}` : ''}${item.locked ? ' plan-obstruction--locked' : ''}`,
          'data-obstruction-id': item.id,
          'data-label-x': inner.x + ni.x * labelGapPx,
          'data-label-y': inner.y + ni.y * labelGapPx,
        }));
        if (item.type === 'wall_opening' && (item.opening?.kind ?? 'swing') === 'swing') {
          const clearWidthUnits = item.opening?.clearOpeningWidthUnits > 0
            ? item.opening.clearOpeningWidthUnits : item.widthUnits;
          const casingInsetUnits = Math.max(0, (item.widthUnits - clearWidthUnits) / 2);
          const swingProjectionUnits = item.opening?.swingProjectionUnits > 0
            ? item.opening.swingProjectionUnits
            : clearWidthUnits + Math.max(0, opts.doorSwingLeafAllowanceUnits ?? 1);
          const swing = doorSwingGeometry({
            p0, p1, along: d, inward: ni, widthPx,
            clearWidthPx: swingProjectionUnits * pxPerUnit, // arc radius: across, not along
            hingeInsetPx: casingInsetUnits * alongPxPerUnit,
            hingeSide: item.opening?.hingeSide,
            swingDirection: item.opening?.swingDirection,
          });
          if (swing) {
            const group = el('g', { class: 'plan-door-swing', 'data-door-swing-id': item.id });
            group.append(
              el('line', {
                x1: swing.hinge.x, y1: swing.hinge.y,
                x2: swing.openEnd.x, y2: swing.openEnd.y,
                class: 'plan-door-swing__leaf',
              }),
              el('path', {
                d: `M ${swing.closedEnd.x.toFixed(1)} ${swing.closedEnd.y.toFixed(1)} A ${swing.radius.toFixed(1)} ${swing.radius.toFixed(1)} 0 0 ${swing.sweep} ${swing.openEnd.x.toFixed(1)} ${swing.openEnd.y.toFixed(1)}`,
                class: 'plan-door-swing__arc',
              }),
            );
            wallLayer.append(group);
          }
        } else if (item.type === 'wall_opening' && (item.opening?.kind ?? '').startsWith('bifold')) {
          const projectionUnits = item.opening?.bifoldStackProjectionUnits >= 0
            ? item.opening.bifoldStackProjectionUnits
            : (opts.bifoldFoldedProjectionUnits ?? 5);
          const stacks = bifoldDoorGeometry({
            p0, p1, inward: ni, projectionPx: projectionUnits * pxPerUnit,
            kind: item.opening.kind, foldSide: item.opening.foldSide,
          });
          if (stacks.length) {
            const group = el('g', { class: 'plan-bifold-stack', 'data-bifold-id': item.id });
            for (const stack of stacks) {
              group.append(el('line', {
                x1: stack.from.x, y1: stack.from.y,
                x2: stack.to.x, y2: stack.to.y,
                class: 'plan-bifold-stack__fold', 'data-fold-side': stack.side,
              }));
            }
            wallLayer.append(group);
          }
        }
      }
    }
  }

  /**
   * Text rotation that follows a direction vector but never reads upside-down
   * (0° and −180° walls both read normally; verticals pinned by sign so
   * floating-point noise can't flip an exact −90° to +90° — ADR 0034/0035).
   */
  function uprightRot(dx, dy) {
    if (Math.abs(dx) <= Math.abs(dy) * 1e-3) return dy >= 0 ? 90 : -90;
    let rot = (Math.atan2(dy, dx) * 180) / Math.PI;
    if (rot > 90) rot -= 180;
    if (rot < -90) rot += 180;
    return rot;
  }

  /**
   * Draws ONE wall's section run on the plan (ADR 0037): each vertical panel is
   * a filled bar standing against the host face and protruding by the sections'
   * depth in the `ni` direction; each opening gets its type code + solved
   * width, rotated with the run and kept upright. Shared between boundary
   * walls (ni = into the room) and partition faces (ni = out from the spur).
   */
  function drawSectionRun({ origin, d, ni, layout, sections, stacks, pxPerUnit, fmt, defaultDepthUnits, wallId, intoHangingAt, alongScale = 1, sectionHangs, hangClearanceUnits = 0, lShelfInfoFor = null, trimEnabled = false, trimEligibleFor = null, trimUnits = 0 }) {
    const secs = sections ?? [];
    // Along the wall, a room unit is worth this much (RS-1): the run is solved
    // in STATED inches, and snap closure may have drawn the wall at a different
    // length. Across the wall — every depth below — the true pxPerUnit stands,
    // because closure bends nothing in that direction.
    const alongPxPerUnit = pxPerUnit * (Number.isFinite(alongScale) && alongScale > 0 ? alongScale : 1);
    const defaultDepth = defaultDepthUnits > 0 ? defaultDepthUnits : 14;
    // Open sections contribute no depth: a divider beside one is only as deep
    // as its REAL neighbour (there is nothing on the open side to flank).
    const secDepth = (i) => (layout.items[i]?.open ? 0 : secs[i]?.depthUnits > 0 ? secs[i].depthUnits : 0);
    const samePosition = (a, b) => Math.abs(a - b) < 0.01;
    // `layout.panels` contains only panels that physically exist; OPEN ends
    // and OPEN→OPEN joins are omitted. Therefore panel index is not section
    // index. Resolve each panel's real neighbours by its solved position.
    const panelDepth = (panel) => {
      const before = layout.items.findIndex((item) =>
        samePosition(item.startUnits + item.openingUnits, panel.startUnits));
      const after = layout.items.findIndex((item) =>
        samePosition(item.startUnits, panel.startUnits + panel.widthUnits));
      return Math.max(secDepth(before), secDepth(after)) || defaultDepth;
    };
    // An overflowing run (locked widths don't fit) flags EVERYTHING in it red
    // on the drawing, not just the side panel (owner, 2026-07-27) — same
    // visual language as the under-minimum warning.
    const overflow = layout.overflowUnits > 0.01;
    layout.panels.forEach((p) => {
      const s = p.startUnits * alongPxPerUnit;
      const wPx = p.widthUnits * alongPxPerUnit;
      const dPx = panelDepth(p) * pxPerUnit;
      const p0 = { x: origin.x + d.x * s, y: origin.y + d.y * s };
      const p1 = { x: p0.x + d.x * wPx, y: p0.y + d.y * wPx };
      const p2 = { x: p1.x + ni.x * dPx, y: p1.y + ni.y * dPx };
      const p3 = { x: p0.x + ni.x * dPx, y: p0.y + ni.y * dPx };
      thicknessLayer.append(el('polygon', { points: pointsAttr([p0, p1, p2, p3]), class: `plan-section-panel${overflow ? ' plan-section-panel--warn' : ''}` }));
    });
    const rot = uprightRot(d.x, d.y);
    // Captions are sized in REAL inches through the drawing scale — a fixed
    // pixel size only looked right on rooms drawn at ~3 px/unit and turned
    // gigantic on a small-drawn room (owner report, 2026-07-26). ~3.4" tall
    // text reads like a drawing annotation at every sketch scale.
    const capPx = (3.4 * pxPerUnit).toFixed(2);

    // The FRONT face of each section, so a unit reads as a closed box rather
    // than as empty space (owner, 2026-08-04, with a mockup). OPEN sections get
    // none — that is exactly what tells them apart on the drawing, and it falls
    // out for free because secDepth() already returns 0 for an open item.
    layout.items.forEach((item, i) => {
      const depth = secDepth(i);
      if (!(depth > 0)) return; // OPEN, or a section with no depth to draw
      const info = typeof lShelfInfoFor === 'function' ? lShelfInfoFor(secs[i]) : null;
      let u0 = item.startUnits;
      let u1 = item.startUnits + item.openingUnits;
      // An attached L-shelf's front face STOPS at the turn (owner, 2026-08-28:
      // one unit, not two overlapping boxes) — beyond it the leg's own front
      // face carries on along the other wall, so drawing the host line through
      // the leg's interior would put a seam inside the L.
      if (info?.join) {
        const trim = Math.min(info.join.legDepthUnits > 0 ? info.join.legDepthUnits : 0, u1 - u0);
        if (info.join.end === 'end') u1 -= trim;
        else u0 += trim;
      }
      // Every L-shelf carries a ¾" backer along the wall (owner, 2026-08-28).
      if (info?.backer && info.backerUnits > 0) {
        const b0 = item.startUnits * alongPxPerUnit;
        const b1 = (item.startUnits + item.openingUnits) * alongPxPerUnit;
        const bPx = info.backerUnits * pxPerUnit;
        thicknessLayer.append(el('polygon', {
          points: pointsAttr([
            { x: origin.x + d.x * b0, y: origin.y + d.y * b0 },
            { x: origin.x + d.x * b1, y: origin.y + d.y * b1 },
            { x: origin.x + d.x * b1 + ni.x * bPx, y: origin.y + d.y * b1 + ni.y * bPx },
            { x: origin.x + d.x * b0 + ni.x * bPx, y: origin.y + d.y * b0 + ni.y * bPx },
          ]),
          class: 'plan-lshelf-backer',
        }));
      }
      if (!(u1 - u0 > 0.01)) return;
      const s0 = u0 * alongPxPerUnit;
      const s1 = u1 * alongPxPerUnit;
      const dPx = depth * pxPerUnit;
      const f0 = { x: origin.x + d.x * s0 + ni.x * dPx, y: origin.y + d.y * s0 + ni.y * dPx };
      const f1 = { x: origin.x + d.x * s1 + ni.x * dPx, y: origin.y + d.y * s1 + ni.y * dPx };
      thicknessLayer.append(el('line', {
        x1: f0.x.toFixed(1), y1: f0.y.toFixed(1), x2: f1.x.toFixed(1), y2: f1.y.toFixed(1),
        class: `plan-section-front${overflow ? ' plan-section-front--warn' : ''}`,
      }));
    });

    // CROWN/BASE TRIM BAND (owner, 2026-08-28, blue-line mockup): when either
    // trim is on, a ¾" band hugs the run's EXPOSED edges — along each front
    // face, jogging out and back where a deeper section protrudes. It follows
    // the same profile the panels/fronts above already draw (a panel stands as
    // deep as its deeper neighbour), sits wholly IN FRONT of it, and breaks at
    // OPEN sections, where there is no carcass to trim over.
    if (trimEnabled === true && trimUnits > 0) {
      // The run as an ordered strip of (start, end, depth) elements:
      // panel 0, item 0, panel 1, item 1, …, panel n. Split at open items;
      // a panel joins the group of a REAL neighbour (a lone panel between two
      // open items trims nothing).
      const groups = [];
      let cur = null;
      const push = (su, eu, dep) => {
        if (!cur) cur = [];
        cur.push({ su, eu, dep });
      };
      // Every group contains an item by construction — panels are only pushed
      // in the same iteration as a REAL item (leading, or trailing when the
      // next item is open/absent) — so a lone panel between two open items
      // never enters a group at all. Pinned by the lone-panel plan test.
      const brk = () => { if (cur) groups.push(cur); cur = null; };
      // `layout.panels` is a compact list of panels that actually exist, not
      // an item-indexed list: OPEN suppresses its wall-end panel. Locate a
      // real item's adjacent panels by their solved positions. Indexing this
      // array by `i` makes every panel after a leading OPEN shift left, so the
      // trim profile walks backward and closes with long diagonals (RS-15).
      const panelBefore = (item) => layout.panels.find((panel) =>
        samePosition(panel.startUnits + panel.widthUnits, item.startUnits));
      const panelAfter = (item) => layout.panels.find((panel) =>
        samePosition(panel.startUnits, item.startUnits + item.openingUnits));
      layout.items.forEach((item, i) => {
        const p = panelBefore(item);
        const dep = secDepth(i);
        const eligible = typeof trimEligibleFor !== 'function'
          || trimEligibleFor(secs[i], stacks?.[i]?.units ?? []);
        if (!(dep > 0) || !eligible) { // open, depthless or wrong unit type
          brk();
          return;
        }
        if (p) push(p.startUnits, p.startUnits + p.widthUnits, panelDepth(p));
        push(item.startUnits, item.startUnits + item.openingUnits, dep);
        const trail = panelAfter(item);
        const next = layout.items[i + 1];
        if (trail && (!next || !(secDepth(i + 1) > 0))) {
          push(trail.startUnits, trail.startUnits + trail.widthUnits, panelDepth(trail));
        }
      });
      brk();

      for (const elems of groups) {
        // Inner boundary: the exposed profile, left to right, deduped.
        const inner = [];
        for (const { su, eu, dep } of elems) {
          const a = [su, dep];
          const b = [eu, dep];
          const last = inner[inner.length - 1];
          if (!last || last[0] !== a[0] || last[1] !== a[1]) inner.push(a);
          inner.push(b);
        }
        // Outer boundary: the profile offset ¾" outward. Horizontal (front)
        // segments move +¾" in depth; vertical (jog) segments move away from
        // the carcass along the wall; rectilinear corners re-intersect by
        // combining the two offsets.
        const t = trimUnits;
        // A vertical (jog) segment's offset direction: stepping DEEPER as we
        // walk right is the LEFT side of a protrusion, whose exposed face
        // looks toward -u; stepping shallower is the RIGHT side, facing +u.
        const seg = (a, b) => (a && b && a[0] === b[0] ? (b[1] > a[1] ? -t : t) : 0);
        const out = inner.map(([u, dep], k) => {
          const du = seg(inner[k - 1], [u, dep]) || seg([u, dep], inner[k + 1]);
          return [u + du, dep + t];
        });
        const toPt = ([u, dep]) => {
          const s = u * alongPxPerUnit;
          const dPx = dep * pxPerUnit;
          return {
            x: origin.x + d.x * s + ni.x * dPx,
            y: origin.y + d.y * s + ni.y * dPx,
          };
        };
        thicknessLayer.append(el('polygon', {
          points: pointsAttr([...inner.map(toPt), ...out.reverse().map(toPt)]),
          class: 'plan-trim',
        }));
      }
    }

    // HANG CLEARANCE (RS-4). A section with a rod in it needs clear space in
    // front of the carcass for the clothes — the admin-editable
    // `section_hang_clearance_units`, 24" by default. The plan drew the carcass
    // depth alone, so a 12" hanging section read as consuming 12" of the room
    // when it actually consumes 24", and the drawing said a walkway fitted
    // where it does not (owner, 2026-08-22).
    //
    // Drawn as a SECOND outline beyond the carcass, never by inflating the
    // carcass: `depthUnits` is the real footprint and drives collision, the
    // corner-clearance solver and the takeoff. Two outlines also say the true
    // thing — the box is this deep, the space it needs is that deep.
    //
    // Hanging is read from the section's COMPOSITION (`sectionHasHang`), not
    // its type code: a designer who strips every rod out of a DH stops needing
    // the clearance and one who adds a rod to an SH starts (ADR 0057).
    if (typeof sectionHangs === 'function' && hangClearanceUnits > 0) {
      layout.items.forEach((item, i) => {
        if (item.open || !secs[i] || sectionHangs(secs[i]) !== true) return;
        const carcass = secDepth(i) || defaultDepth;
        if (!(hangClearanceUnits > carcass + 0.01)) return; // already deep enough
        const s0 = item.startUnits * alongPxPerUnit;
        const s1 = (item.startUnits + item.openingUnits) * alongPxPerUnit;
        const cPx = hangClearanceUnits * pxPerUnit;
        const carcassPx = carcass * pxPerUnit;
        const corner = (along, depthPx) => ({
          x: origin.x + d.x * along + ni.x * depthPx,
          y: origin.y + d.y * along + ni.y * depthPx,
        });
        thicknessLayer.append(el('polygon', {
          points: pointsAttr([corner(s0, carcassPx), corner(s1, carcassPx), corner(s1, cPx), corner(s0, cPx)]),
          class: 'plan-hang-clearance',
        }));
        const midPx = (item.startUnits + item.openingUnits / 2) * alongPxPerUnit;
        const t = el('text', {
          transform: `translate(${corner(midPx, (carcassPx + cPx) / 2).x.toFixed(1)},${corner(midPx, (carcassPx + cPx) / 2).y.toFixed(1)}) rotate(${rot.toFixed(1)})`,
          // NOT `section-note`: that class means "a section's caption" to the
          // drag/reorder code and to every selector that counts sections. This
          // labels required SPACE, not a unit, and is not draggable.
          class: 'plan-clearance-note',
          'text-anchor': 'middle',
          'font-size': (2.3 * pxPerUnit).toFixed(2),
        });
        t.textContent = `${fmt ? fmt(hangClearanceUnits) : hangClearanceUnits} hang`;
        thicknessLayer.append(t);
      });
    }

    layout.items.forEach((item, i) => {
      const depth = secDepth(i) || defaultDepth;
      const midPx = (item.startUnits + item.openingUnits / 2) * alongPxPerUnit;
      const inPx = (depth / 2) * pxPerUnit;
      const px = origin.x + d.x * midPx + ni.x * inPx;
      const py = origin.y + d.y * midPx + ni.y * inPx;
      // Hanging-into-hanging, at EITHER corner (the flag is per-endpoint).
      // Read through an injected callback, never a `model` this module can see:
      // canvas.js renders what it is handed and owns no state (see the file
      // header), and a bare `model.` here resolved to nothing at runtime — the
      // whole plan render threw before it drew a single wall.
      const into = intoHangingAt?.(secs[i], 'start') === true
        || intoHangingAt?.(secs[i], 'end') === true;
      const warn = item.narrow || item.wide || overflow || into;
      const t = el('text', {
        transform: `translate(${px.toFixed(1)},${py.toFixed(1)}) rotate(${rot.toFixed(1)})`,
        class: `section-note${warn ? ' section-note--warn' : ''}${item.open ? ' section-note--open' : ''}`,
        'text-anchor': 'middle',
        'font-size': capPx,
      });
      // The measurement sits BELOW the type code, smaller (owner preference) —
      // the code is the label, the width is detail. Both tspans reset x=0 so
      // each line centres on the caption's anchor independently.
      const codeSpan = el('tspan', { x: 0 });
      codeSpan.textContent = (secs[i]?.typeCode ?? '?') + (into ? ' ⚠' : '');
      const meas = el('tspan', { x: 0, dy: (3 * pxPerUnit).toFixed(2), 'font-size': (2.3 * pxPerUnit).toFixed(2) });
      meas.textContent = fmt ? fmt(item.openingUnits) : String(Math.round(item.openingUnits));
      t.append(codeSpan, meas);
      // Drag the caption along the run to REORDER the section (owner,
      // 2026-07-26 — reorder on the drawing, not just the side panel). The
      // caption slides with the finger; the drop slot is where it lands
      // relative to the OTHER openings' midpoints.
      const sec = secs[i];
      if (sec && wallId) {
        t.addEventListener('pointerdown', (evt) => beginSectionDrag(evt, {
          id: sec.id,
          wallId,
          index: i,
          ctx: { start: { ...origin }, d, ni, gap: inPx, rot, pxPerRunUnit: alongPxPerUnit, lenPx: layout.runUnits * alongPxPerUnit },
          others: layout.items.filter((_, j) => j !== i).map((o) => o.startUnits + o.openingUnits / 2),
        }));
      }
      thicknessLayer.append(t);
    });

    // Units STACKED on a run section (ADR 0041). On a top-down plan they sit
    // over the unit below, so they are drawn as an outline at their OWN width,
    // position and depth — a shallower/narrower unit reads as a smaller
    // footprint inside the base's (owner: the plan must show all units).
    (stacks ?? []).forEach((stack, i) => {
      const units = stack?.units ?? [];
      for (let k = 1; k < units.length; k += 1) {
        const u = units[k];
        const item = layout.items[i];
        if (!item) continue;
        const wU = u.widthUnits > 0 ? u.widthUnits : item.openingUnits;
        const xU = item.startUnits + (u.offsetUnits > 0 ? u.offsetUnits : 0);
        const dU = u.depthUnits > 0 ? u.depthUnits : defaultDepth;
        const s0 = xU * alongPxPerUnit;
        const wPx = wU * alongPxPerUnit;
        const dPx = dU * pxPerUnit;
        const q0 = { x: origin.x + d.x * s0, y: origin.y + d.y * s0 };
        const q1 = { x: q0.x + d.x * wPx, y: q0.y + d.y * wPx };
        const q2 = { x: q1.x + ni.x * dPx, y: q1.y + ni.y * dPx };
        const q3 = { x: q0.x + ni.x * dPx, y: q0.y + ni.y * dPx };
        thicknessLayer.append(el('polygon', {
          points: pointsAttr([q0, q1, q2, q3]),
          class: 'plan-stacked-unit',
        }));
        // Its code, set in from the wall so it doesn't collide with the run's
        // caption (which sits at the run section's mid-depth).
        const midPx = (xU + wU / 2) * alongPxPerUnit;
        const inPx = (dU * 0.28) * pxPerUnit;
        const st = el('text', {
          transform: `translate(${(origin.x + d.x * midPx + ni.x * inPx).toFixed(1)},${(origin.y + d.y * midPx + ni.y * inPx).toFixed(1)}) rotate(${rot.toFixed(1)})`,
          class: 'section-note section-note--stacked',
          'text-anchor': 'middle',
          'font-size': (2.6 * pxPerUnit).toFixed(2),
        });
        st.textContent = u.section?.typeCode ?? '?';
        thicknessLayer.append(st);
      }
    });

    if (overflow) {
      // The run's verdict, unmissable on the drawing: how much doesn't fit.
      const midPx = (layout.runUnits / 2) * alongPxPerUnit;
      const inPx = (Math.max(...layout.items.map((_, i) => secDepth(i) || defaultDepth)) + 5) * pxPerUnit;
      const t = el('text', {
        transform: `translate(${(origin.x + d.x * midPx + ni.x * inPx).toFixed(1)},${(origin.y + d.y * midPx + ni.y * inPx).toFixed(1)}) rotate(${rot.toFixed(1)})`,
        class: 'section-note section-note--warn',
        'text-anchor': 'middle',
        'font-size': capPx,
      });
      t.textContent = `▲ over by ${fmt ? fmt(layout.overflowUnits) : Math.round(layout.overflowUnits)}`;
      thicknessLayer.append(t);
    }
  }

  /** Start dragging a section caption to reorder it (Select mode only). */
  function beginSectionDrag(evt, drag) {
    if (mode !== 'select') return;
    evt.stopPropagation();
    sectionDrag = { ...drag, node: evt.currentTarget, slot: null };
    svg.setPointerCapture(evt.pointerId);
  }

  /**
   * Wall sections on the plan (ADR 0037): the solved section run of every
   * boundary wall that has one. Partition-face runs are drawn by
   * renderPartitions, which owns the spur geometry.
   */
  function renderPlanSections(chain, opts = {}) {
    const { sectionLayoutFor, sectionsFor, pxPerUnit, formatLength, connectingShelvesFor } = opts;
    if (typeof sectionLayoutFor !== 'function' || !pxPerUnit || pxPerUnit <= 0) return;
    const inward = inwardNormalFn(chain);
    for (const w of chain) {
      if ((w.segmentKind ?? 'wall') !== 'wall') continue;
      const layout = sectionLayoutFor(w.id);
      // An L-shelf leg reaches onto this wall from a NEIGHBOUR's run, so it
      // must draw whether or not this wall has a run of its own — the guard
      // below once skipped run-less walls entirely and the leg vanished on
      // any empty attached wall (owner's Pantry, 2026-08-28).
      const legs = opts.lShelfLegsFor?.(w.id) ?? [];
      if (!layout && legs.length === 0) continue;
      const dx = w.end.x - w.start.x;
      const dy = w.end.y - w.start.y;
      const len = Math.hypot(dx, dy) || 1;
      const d = { x: dx / len, y: dy / len };
      const ni = inward(w);
      // Everything on this wall — run, connecting shelves, L-shelf legs — is
      // drawn to the wall AS DRAWN, so it all shares one factor (RS-1).
      const alongScale = alongWallScale(w, opts);
      const alongPxPerUnit = pxPerUnit * alongScale;
      if (layout) drawSectionRun({
        origin: w.start,
        d,
        ni,
        wallId: w.id,
        layout,
        sections: sectionsFor?.(w.id) ?? [],
        stacks: opts.stacksFor?.(w.id) ?? [],
        pxPerUnit,
        fmt: formatLength,
        defaultDepthUnits: opts.sectionDefaultDepthUnits,
        intoHangingAt: opts.sectionIntoHangingAt,
        alongScale,
        sectionHangs: opts.sectionHangsFor,
        hangClearanceUnits: opts.sectionHangClearanceUnits,
        lShelfInfoFor: opts.lShelfInfoFor,
        trimEnabled: opts.planTrimEnabled === true,
        trimEligibleFor: opts.planTrimEligibleFor,
        trimUnits: opts.planTrimUnits > 0 ? opts.planTrimUnits : 0.75,
      });

      // Connecting shelves (ADR 0038): auto shelf bridging the corner between
      // a neighbour's hanging section and this wall's outermost panel.
      for (const shelf of connectingShelvesFor?.(w.id) ?? []) {
        const s0 = shelf.fromUnits * alongPxPerUnit;
        const s1 = shelf.toUnits * alongPxPerUnit;
        const dPx = shelf.depthUnits * pxPerUnit;
        const p0 = { x: w.start.x + d.x * s0, y: w.start.y + d.y * s0 };
        const p1 = { x: w.start.x + d.x * s1, y: w.start.y + d.y * s1 };
        const p2 = { x: p1.x + ni.x * dPx, y: p1.y + ni.y * dPx };
        const p3 = { x: p0.x + ni.x * dPx, y: p0.y + ni.y * dPx };
        if (shelf.topShelf !== false || shelf.bottomShelf !== false) {
          thicknessLayer.append(el('polygon', { points: pointsAttr([p0, p1, p2, p3]), class: 'plan-connecting-shelf' }));
        }
        const midPx = (s0 + s1) / 2;
        const inPx = dPx / 2;
        const t = el('text', {
          transform: `translate(${(w.start.x + d.x * midPx + ni.x * inPx).toFixed(1)},${(w.start.y + d.y * midPx + ni.y * inPx).toFixed(1)}) rotate(${uprightRot(d.x, d.y).toFixed(1)})`,
          class: 'section-note section-note--shelf',
          'text-anchor': 'middle',
          'font-size': (2.8 * pxPerUnit).toFixed(2), // real-inch sizing, like the section captions
        });
        const label = shelf.topShelf === false && shelf.bottomShelf === false ? 'OPEN' : 'CS';
        t.textContent = `${label} ${formatLength ? formatLength(shelf.widthUnits) : Math.round(shelf.widthUnits)}`;
        thicknessLayer.append(t);
      }

      // Open-span sizes (owner, 2026-08-28): each gap the solver reports —
      // slack before a corner-pinned L-shelf, or plain unfilled run — shows
      // its length so the designer reads the available wall straight off the
      // plan. Gated by the plan_open_span_label_min_units rule, passed by the
      // caller: no threshold, no labels (the measurement sheet stays as-is).
      if (layout && Number.isFinite(opts.openSpanLabelMinUnits)) {
        for (const gap of layout.gaps ?? []) {
          if (!(gap.widthUnits >= opts.openSpanLabelMinUnits)) continue;
          const midPx = (gap.startUnits + gap.widthUnits / 2) * alongPxPerUnit;
          const inPx = 4 * pxPerUnit;
          const t = el('text', {
            transform: `translate(${(w.start.x + d.x * midPx + ni.x * inPx).toFixed(1)},${(w.start.y + d.y * midPx + ni.y * inPx).toFixed(1)}) rotate(${uprightRot(d.x, d.y).toFixed(1)})`,
            // NOT `section-note`: that class means "a section's caption" and
            // is counted/dragged as one (RS-4 pinned it). Open wall is not a
            // section; this label has its own class and no drag affordance.
            class: 'plan-open-span',
            'text-anchor': 'middle',
            'font-size': (2.8 * pxPerUnit).toFixed(2),
          });
          t.textContent = formatLength ? formatLength(gap.widthUnits) : String(Math.round(gap.widthUnits));
          thicknessLayer.append(t);
        }
      }

      // L-shelf second legs (ADR 0042): the leg of a corner L-shelf that lies
      // along THIS wall while the shelf itself lives in the neighbouring wall's
      // run. The run already reserves the space; this draws what occupies it —
      // and it draws with or without a run on this wall (see the loop guard).
      // Drawn in the HOST section's visual language so the L-shelf reads as
      // ONE unit, not two overlapping boxes (owner, 2026-08-28): no fill, no
      // second caption — a ¾" backer along the wall, the leg's single far-end
      // panel (an L-shelf has exactly two end panels; the corner side is
      // continuous), and a front-face line running from where the host body
      // turns to where the end panel caps it.
      for (const leg of legs) {
        const rect = (u0, u1, d0, d1, cls) => {
          const a0 = u0 * alongPxPerUnit;
          const a1 = u1 * alongPxPerUnit;
          const p0 = { x: w.start.x + d.x * a0 + ni.x * d0 * pxPerUnit, y: w.start.y + d.y * a0 + ni.y * d0 * pxPerUnit };
          const p1 = { x: w.start.x + d.x * a1 + ni.x * d0 * pxPerUnit, y: w.start.y + d.y * a1 + ni.y * d0 * pxPerUnit };
          const p2 = { x: w.start.x + d.x * a1 + ni.x * d1 * pxPerUnit, y: w.start.y + d.y * a1 + ni.y * d1 * pxPerUnit };
          const p3 = { x: w.start.x + d.x * a0 + ni.x * d1 * pxPerUnit, y: w.start.y + d.y * a0 + ni.y * d1 * pxPerUnit };
          thicknessLayer.append(el('polygon', { points: pointsAttr([p0, p1, p2, p3]), class: cls }));
        };
        const pt = leg.endPanel ? leg.endPanel.toUnits - leg.endPanel.fromUnits : 0.75;
        rect(leg.fromUnits, leg.toUnits, 0, pt, 'plan-lshelf-backer');
        if (leg.endPanel) rect(leg.endPanel.fromUnits, leg.endPanel.toUnits, 0, leg.depthUnits, 'plan-section-panel plan-lshelf-end-panel');
        // Front face: from the turn (the host body's depth into this wall,
        // measured from the corner) to the end panel. Degenerate legs —
        // shorter than the host is deep — draw no face.
        const boardEnd = leg.cornerAt === 'to' ? (leg.endPanel?.toUnits ?? leg.fromUnits) : (leg.endPanel?.fromUnits ?? leg.toUnits);
        const junction = leg.cornerAt === 'to'
          ? leg.toUnits - (leg.hostDepthUnits ?? 0)
          : leg.fromUnits + (leg.hostDepthUnits ?? 0);
        const u0 = Math.min(junction, boardEnd);
        const u1 = Math.max(junction, boardEnd);
        if (u1 - u0 > 0.01) {
          const dPx = leg.depthUnits * pxPerUnit;
          const f0 = { x: w.start.x + d.x * u0 * alongPxPerUnit + ni.x * dPx, y: w.start.y + d.y * u0 * alongPxPerUnit + ni.y * dPx };
          const f1 = { x: w.start.x + d.x * u1 * alongPxPerUnit + ni.x * dPx, y: w.start.y + d.y * u1 * alongPxPerUnit + ni.y * dPx };
          thicknessLayer.append(el('line', {
            x1: f0.x.toFixed(1), y1: f0.y.toFixed(1), x2: f1.x.toFixed(1), y2: f1.y.toFixed(1),
            class: 'plan-section-front plan-lshelf-leg',
          }));
        }
      }
    }
  }

  /**
   * Interior / partition walls (ADR 0032): the spur that sticks INTO the room.
   * Its base is the footprint segment (an `opening` in the chain, drawn solid by
   * the boundary pass); from there it runs inward by the partition's depth, the
   * full thickness wide, capped flat at the free end. Both long faces are the
   * usable cabinet faces. Selecting it (tap) selects the footprint segment so
   * its depth shows in the side panel.
   */
  function renderPartitions(chain, selection, selectWall, opts = {}) {
    const { partitionIds, partitionDepthFor, pxPerUnit, partitionFaces, wallLabelsFor, obstructionsFor, sectionLayoutFor, sectionsFor } = opts;
    if (!partitionIds || partitionIds.size === 0 || !pxPerUnit || pxPerUnit <= 0) return;
    const inward = inwardNormalFn(chain);
    // Group the two numbered faces by their partition, for on-plan labels.
    const facesBySeg = new Map();
    for (const f of partitionFaces ?? []) {
      const list = facesBySeg.get(f.partitionSegmentId) ?? [];
      list.push(f);
      facesBySeg.set(f.partitionSegmentId, list);
    }

    for (const seg of chain) {
      if (!partitionIds.has(seg.id)) continue;
      const depthUnits = typeof partitionDepthFor === 'function' ? partitionDepthFor(seg.id) : null;
      if (!(depthUnits > 0)) continue;

      const ni = inward(seg); // into the room
      const depthPx = depthUnits * pxPerUnit;
      const baseL = { x: seg.start.x, y: seg.start.y };
      const baseR = { x: seg.end.x, y: seg.end.y };
      const tipL = { x: baseL.x + ni.x * depthPx, y: baseL.y + ni.y * depthPx };
      const tipR = { x: baseR.x + ni.x * depthPx, y: baseR.y + ni.y * depthPx };
      const selected = seg.id === selection;
      const poly = el('polygon', {
        points: pointsAttr([baseL, baseR, tipR, tipL]),
        class: `wall-band partition-band${selected ? ' wall-band--selected' : ''}`,
        'data-partition-id': seg.id,
      });
      poly.addEventListener('pointerdown', selectWall(seg.id));
      wallLayer.append(poly);

      // Label each numbered face at its midpoint, pushed to its OUTER long side
      // so the two chips don't collide on the thin spur.
      const dx = seg.end.x - seg.start.x;
      const dy = seg.end.y - seg.start.y;
      const along = Math.hypot(dx, dy) || 1;
      const d = { x: dx / along, y: dy / along }; // seg.start → seg.end
      const gap = pxPerUnit * 6; // ~6" clear of the face
      for (const f of facesBySeg.get(seg.id) ?? []) {
        const start = f.side === 'A' ? baseL : baseR;
        const end = f.side === 'A' ? tipL : tipR;
        const out = f.side === 'A' ? { x: -d.x, y: -d.y } : { x: d.x, y: d.y };
        labelAnchors.push({
          x: (start.x + end.x) / 2 + out.x * gap,
          y: (start.y + end.y) / 2 + out.y * gap,
          number: `W${f.wallNumber}`,
          length: f.lengthLabel ?? '—',
          selected: false,
        });

        // Section labels (DH, LH…) anchored to THIS face, along the spur.
        const labels = typeof wallLabelsFor === 'function' ? wallLabelsFor(f.id) ?? [] : [];
        if (labels.length) {
          // The face runs along the inward normal `ni`; keep the text upright.
          let rot;
          if (Math.abs(ni.x) <= Math.abs(ni.y) * 1e-3) rot = ni.y >= 0 ? 90 : -90;
          else { rot = (Math.atan2(ni.y, ni.x) * 180) / Math.PI; if (rot > 90) rot -= 180; if (rot < -90) rot += 180; }
          const ctx = { start: { ...start }, d: ni, len: depthPx, ni: out, gap, rot, pxPerUnit };
          for (const label of labels) {
            const a = label.offsetAlongWall * pxPerUnit;
            const px = start.x + ni.x * a + out.x * gap;
            const py = start.y + ni.y * a + out.y * gap;
            const t = el('text', {
              transform: `translate(${px.toFixed(1)},${py.toFixed(1)}) rotate(${rot.toFixed(1)})`,
              class: 'wall-label-note', 'text-anchor': 'middle', 'data-wall-label-id': label.id,
            });
            t.style.fontSize = `${(5 * pxPerUnit).toFixed(2)}px`; // real-inch sizing on the plan
            t.textContent = label.text;
            t.addEventListener('pointerdown', (evt) => beginLabelDrag(evt, label.id, ctx));
            t.addEventListener('contextmenu', (evt) => { evt.preventDefault(); handlers.onLabelMenu?.(label.id, evt.clientX, evt.clientY); });
            wallLayer.append(t);
          }
        }

        // Section run anchored to THIS face (ADR 0037): same shared renderer as
        // a boundary wall — along the face (ni), panels protruding out of the
        // spur (out) — so a partition's two cabinet faces section like walls.
        if (typeof sectionLayoutFor === 'function') {
          const faceLayout = sectionLayoutFor(f.id);
          if (faceLayout) {
            drawSectionRun({
              origin: start,
              d: ni,
              ni: out,
              wallId: f.id,
              layout: faceLayout,
              sections: sectionsFor?.(f.id) ?? [],
              stacks: opts.stacksFor?.(f.id) ?? [],
              pxPerUnit,
              fmt: opts.formatLength,
              defaultDepthUnits: opts.sectionDefaultDepthUnits,
              intoHangingAt: opts.sectionIntoHangingAt,
              sectionHangs: opts.sectionHangsFor,
              hangClearanceUnits: opts.sectionHangClearanceUnits,
              trimEnabled: opts.planTrimEnabled === true,
              trimEligibleFor: opts.planTrimEligibleFor,
              trimUnits: opts.planTrimUnits > 0 ? opts.planTrimUnits : 0.75,
            });
          }
        }

        // Obstructions anchored to THIS face, drawn on the spur: they run ALONG
        // the face (the inward normal) and protrude OUTWARD from the spur (out),
        // mirroring how a wall's obstructions run along it and protrude inward.
        const faceItems = typeof obstructionsFor === 'function' ? obstructionsFor(f.id) ?? [] : [];
        const faceLanes = assignLabelLanes(faceItems);
        for (const item of faceItems) {
          const alongPx = item.offsetAlongWall * pxPerUnit;
          const widthPx = item.widthUnits * pxPerUnit;
          const depthU = Number.isFinite(item.depthUnits) ? item.depthUnits : DEFAULT_OBSTRUCTION_DEPTH_UNITS;
          const depthPx = depthU * pxPerUnit;
          const facePx = (Number.isFinite(item.faceOffsetUnits) ? item.faceOffsetUnits : 0) * pxPerUnit;
          const near = { x: start.x + ni.x * alongPx + out.x * facePx, y: start.y + ni.y * alongPx + out.y * facePx };
          const p1 = { x: near.x + ni.x * widthPx, y: near.y + ni.y * widthPx };
          const p2 = { x: p1.x + out.x * depthPx, y: p1.y + out.y * depthPx };
          const p3 = { x: near.x + out.x * depthPx, y: near.y + out.y * depthPx };
          // Clear of the spur, on the side the fixture protrudes toward — the
          // same rule as a wall's, with `out` playing the inward normal's part.
          const faceInner = { x: (p2.x + p3.x) / 2, y: (p2.y + p3.y) / 2 };
          const faceGapPx = OBSTRUCTION_LABEL_GAP_PX
            + (faceLanes.get(item.id) ?? 0) * OBSTRUCTION_LABEL_LANE_PX;
          thicknessLayer.append(el('polygon', {
            points: pointsAttr([near, p1, p2, p3]),
            class: `elev-obstruction elev-obstruction--${item.type} plan-obstruction${item.type === 'wall_opening' ? ` plan-opening--${item.opening?.kind ?? 'swing'}` : ''}${item.locked ? ' plan-obstruction--locked' : ''}`,
            'data-obstruction-id': item.id,
            'data-label-x': faceInner.x + out.x * faceGapPx,
            'data-label-y': faceInner.y + out.y * faceGapPx,
          }));
        }
      }
    }
  }

  /** Draw the annotations for this surface: freehand strokes AND text notes. */
  function renderAnnotations(strokes = []) {
    annotations = strokes;
    annotationLayer.replaceChildren();
    for (const a of strokes) {
      if (a.kind === 'text') {
        const p = a.points?.[0];
        if (!p) continue;
        const t = el('text', {
          x: p.x, y: p.y,
          class: 'annotation-text',
          fill: a.color,
          'data-annotation-id': a.id,
        });
        // Size is stored in INCHES and painted at the drawing's scale, so a note
        // keeps its size relative to the room rather than ballooning on a
        // small-drawn sketch (the same fix the section captions carry). Without
        // one, the stylesheet's default applies.
        //
        // It must be an INLINE style: `.annotation-text` sets font-size in the
        // stylesheet, and a stylesheet rule beats an SVG presentation attribute,
        // so setting the attribute changed the value and nothing on screen
        // (owner, 2026-07-29).
        if (a.fontSize > 0) t.style.fontSize = `${(a.fontSize * (lastPxPerUnit || 1)).toFixed(2)}px`;
        t.textContent = a.text ?? '';
        t.addEventListener('pointerdown', (evt) => beginTextDrag(evt, a.id, p));
        t.addEventListener('contextmenu', (evt) => { evt.preventDefault(); handlers.onTextMenu?.(a.id, evt.clientX, evt.clientY); });
        annotationLayer.append(t);
      } else {
        annotationLayer.append(el('polyline', {
          points: pointsAttr(a.points),
          class: 'annotation',
          stroke: a.color,
          'stroke-width': a.width,
          'data-annotation-id': a.id,
        }));
      }
    }
  }

  function setAnnotationStyle(style = {}) {
    annotationStyle = { ...annotationStyle, ...style };
    // A colour picked mid-stroke should reach the line being drawn, not the
    // next one.
    if (drawing?.annotation) penPreviewStyle();
  }

  function setAnnotationStraight(on) {
    annotationStraight = Boolean(on);
  }

  /** Shortest distance from p to a polyline (its nearest segment), in model px. */
  function distToStroke(p, pts) {
    let min = Infinity;
    for (let i = 1; i < pts.length; i++) min = Math.min(min, distToSegment(p, pts[i - 1], pts[i]));
    if (pts.length === 1) min = distance(p, pts[0]);
    return min;
  }

  function distToSegment(p, a, b) {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len2 = dx * dx + dy * dy;
    if (len2 === 0) return distance(p, a);
    let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
    return distance(p, { x: a.x + t * dx, y: a.y + t * dy });
  }

  /** In erase mode, remove the topmost annotation under the pointer, if any. */
  function eraseAt(clientX, clientY) {
    const p = toModelPoint(clientX, clientY);
    const tol = 8 / view.scale; // ~8 screen px, in model units
    // Tape measurements erase like anything else (owner: visible until deleted).
    for (let i = measures.length - 1; i >= 0; i--) {
      if (distToStroke(p, [measures[i].a, measures[i].b]) <= tol) {
        measures.splice(i, 1);
        renderMeasures();
        return;
      }
    }
    // Topmost first (last drawn), so the eraser takes what looks on top.
    for (let i = annotations.length - 1; i >= 0; i--) {
      const a = annotations[i];
      // A text note is hit-tested against its RENDERED box (its single anchor
      // point sits at the bottom-left, so proximity to it alone would miss).
      if (a.kind === 'text') {
        const node = annotationLayer.querySelector(`[data-annotation-id="${a.id}"]`);
        const box = node?.getBoundingClientRect();
        if (box && clientX >= box.left - 6 && clientX <= box.right + 6 && clientY >= box.top - 6 && clientY <= box.bottom + 6) {
          handlers.onEraseAnnotation?.(a.id);
          return;
        }
        continue;
      }
      if (distToStroke(p, a.points) <= tol + a.width / 2) {
        handlers.onEraseAnnotation?.(a.id);
        return;
      }
    }
  }

  // ---- view controls -------------------------------------------------------

  function setMode(next) {
    mode = next;
    svg.dataset.mode = next;
    // Measurements deliberately SURVIVE mode changes (owner: visible until
    // deleted, several at once). The eraser tool removes them.
  }

  /**
   * Tape measure (owner, 2026-07-27): drag between two points, read the true
   * distance. Lives in model coordinates so it scales with the drawing at any
   * zoom (like the grid). Measurements accumulate and stay visible until the
   * ERASER removes them (owner: multiple readings at once); they are session
   * ephemera, never persisted to the sketch.
   */
  function renderMeasures() {
    measureLayer.replaceChildren();
    for (const mm of [...measures, ...(activeMeasure ? [activeMeasure] : [])]) {
      const { a, b } = mm;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const lenPx = Math.hypot(dx, dy);
      measureLayer.append(el('line', { x1: a.x, y1: a.y, x2: b.x, y2: b.y, class: 'measure-line' }));
      // End ticks perpendicular to the line, like a dimension line.
      if (lenPx > 1) {
        const nx = -dy / lenPx;
        const ny = dx / lenPx;
        const tick = Math.max(4, (lastPxPerUnit || 1) * 2); // ~2" ticks
        for (const p of [a, b]) {
          measureLayer.append(el('line', {
            x1: p.x - nx * tick, y1: p.y - ny * tick, x2: p.x + nx * tick, y2: p.y + ny * tick,
            class: 'measure-line',
          }));
        }
      }
      const label = el('text', {
        transform: `translate(${((a.x + b.x) / 2).toFixed(1)},${((a.y + b.y) / 2).toFixed(1)}) rotate(${uprightRot(dx, dy).toFixed(1)})`,
        class: 'measure-label',
        'text-anchor': 'middle',
        dy: (-(lastPxPerUnit || 1) * 1.2).toFixed(1), // sit just above the line
        'font-size': ((lastPxPerUnit || 1) * 2.4).toFixed(2), // quieter than captions (owner)
      });
      label.textContent = lastPxPerUnit > 0
        ? (lastFmt ? lastFmt(lenPx / lastPxPerUnit) : `${Math.round(lenPx / lastPxPerUnit)}"`)
        : 'no scale yet — set a wall length';
      measureLayer.append(label);
    }
  }

  /**
   * Begin dragging a text note to reposition it — only in Select mode, so in
   * draw/annotate/erase the tap falls through to those tools. The svg's
   * move/end handlers finish the drag and commit the new anchor via onMoveText.
   */
  function beginTextDrag(evt, id, anchor) {
    if (mode !== 'select') return;
    evt.stopPropagation();
    textDrag = {
      id,
      node: evt.currentTarget,
      orig: { x: anchor.x, y: anchor.y },
      grab: toModelPoint(evt.clientX, evt.clientY),
      last: null,
    };
    svg.setPointerCapture(evt.pointerId);
  }

  /**
   * Text tool: float a one-line input where the user tapped, and on Enter/blur
   * hand the typed note + its MODEL anchor to onAddText. Escape cancels. The
   * input is positioned over the stage (which is position:relative).
   */
  function promptTextAt(clientX, clientY) {
    const modelPoint = toModelPoint(clientX, clientY);
    floatTextInput(clientX, clientY, annotationStyle.color, (value) => {
      if (value.trim()) handlers.onAddText?.(modelPoint, value, { ...annotationStyle });
    });
  }

  function fit(points) {
    const { w, h } = viewBox();
    // Wall-number chips live outside the transformed viewport at a constant
    // screen size. Their anchors therefore have to participate in fitting;
    // the normal 70px fit padding then reserves the chip's full 38px height
    // and its maximum 130px width as well as the room geometry.
    view = fitPoints([...(points ?? []), ...labelAnchors, ...ceilingOpeningLabelAnchors], w, h);
    applyView();
  }

  function zoomBy(factor, atClientX, atClientY) {
    const p = atClientX == null ? centerSvgPoint() : toSvgPoint(atClientX, atClientY);
    view = zoomAt(view, view.scale * factor, p.x, p.y);
    applyView();
  }

  function centerSvgPoint() {
    const { w, h } = viewBox();
    return { x: w / 2, y: h / 2 };
  }

  // ---- pointer handling ----------------------------------------------------

  function pinchMetrics() {
    const [a, b] = [...activePointers.values()];
    return { dist: Math.hypot(a.x - b.x, a.y - b.y), mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } };
  }

  svg.addEventListener('pointerdown', (evt) => {
    activePointers.set(evt.pointerId, { x: evt.clientX, y: evt.clientY });

    if (activePointers.size === 2) {
      // A pinch supersedes any single-pointer gesture.
      drawing = null;
      panDrag = null;
      activeStroke.setAttribute('points', '');
      const { dist: d, mid } = pinchMetrics();
      pinch = { startDist: d, startMid: mid, startView: { ...view } };
      return;
    }
    if (activePointers.size > 2) return;

    if (mode === 'pan') {
      panDrag = { last: toSvgPoint(evt.clientX, evt.clientY) };
      svg.setPointerCapture(evt.pointerId);
    } else if (mode === 'draw') {
      drawing = { points: [toModelPoint(evt.clientX, evt.clientY)] };
      activeStroke.setAttribute('points', pointsAttr(drawing.points));
      wallGuideStyle();
      svg.setPointerCapture(evt.pointerId);
    } else if (mode === 'annotate') {
      // Freehand (or straight-line) design stroke over the room. Same capture
      // as drawing walls, but styled in the pen colour and routed to onAnnotate.
      const start = toModelPoint(evt.clientX, evt.clientY);
      drawing = { points: [start], annotation: true, straight: annotationStraight, anchor: start };
      activeStroke.setAttribute('points', pointsAttr(drawing.points));
      penPreviewStyle();
      svg.setPointerCapture(evt.pointerId);
    } else if (mode === 'text') {
      // Typed note: drop a text field where tapped; commit on Enter/blur.
      promptTextAt(evt.clientX, evt.clientY);
    } else if (mode === 'measure') {
      const p = toModelPoint(evt.clientX, evt.clientY);
      activeMeasure = { a: p, b: p };
      renderMeasures();
      svg.setPointerCapture(evt.pointerId);
    } else if (mode === 'erase') {
      erasing = true;
      eraseAt(evt.clientX, evt.clientY);
      svg.setPointerCapture(evt.pointerId);
    } else if (mode === 'select') {
      // A click on empty canvas clears the selection; a click on a wall is
      // handled by the wall's own listener, which stops propagation.
      handlers.onSelectWall?.(null);
    }
  });

  svg.addEventListener('pointermove', (evt) => {
    if (activePointers.has(evt.pointerId)) activePointers.set(evt.pointerId, { x: evt.clientX, y: evt.clientY });

    if (textDrag) {
      const now = toModelPoint(evt.clientX, evt.clientY);
      const nx = textDrag.orig.x + (now.x - textDrag.grab.x);
      const ny = textDrag.orig.y + (now.y - textDrag.grab.y);
      textDrag.node.setAttribute('x', nx);
      textDrag.node.setAttribute('y', ny);
      textDrag.last = { x: nx, y: ny };
      return;
    }

    if (activeMeasure) {
      activeMeasure.b = toModelPoint(evt.clientX, evt.clientY);
      renderMeasures();
      return;
    }

    if (sectionDrag) {
      const p = toModelPoint(evt.clientX, evt.clientY);
      const { start, d, ni, gap, rot, pxPerRunUnit, lenPx } = sectionDrag.ctx;
      let t = (p.x - start.x) * d.x + (p.y - start.y) * d.y; // project onto the run
      t = Math.max(0, Math.min(lenPx, t));
      const px = start.x + d.x * t + ni.x * gap;
      const py = start.y + d.y * t + ni.y * gap;
      sectionDrag.node.setAttribute('transform', `translate(${px.toFixed(1)},${py.toFixed(1)}) rotate(${rot.toFixed(1)})`);
      // The drop slot: how many OTHER openings' midpoints sit before the drop.
      // Those midpoints are in RUN units, so the projection comes back through
      // the run's own along-wall scale, not the drawing's plain pxPerUnit.
      const along = t / pxPerRunUnit;
      sectionDrag.slot = sectionDrag.others.filter((mid) => mid < along).length;
      return;
    }

    if (labelDrag) {
      const p = toModelPoint(evt.clientX, evt.clientY);
      const { start, d, len, ni, gap, rot, pxPerRunUnit } = labelDrag.ctx;
      let t = (p.x - start.x) * d.x + (p.y - start.y) * d.y; // project onto the wall
      t = Math.max(0, Math.min(len, t));
      const px = start.x + d.x * t + ni.x * gap;
      const py = start.y + d.y * t + ni.y * gap;
      labelDrag.node.setAttribute('transform', `translate(${px.toFixed(1)},${py.toFixed(1)}) rotate(${rot.toFixed(1)})`);
      // Back through the SAME factor the label was drawn with (D98): `t` is a
      // distance on the DRAWN wall, and `offsetAlongWall` is stated inches.
      // Dividing by the plain pxPerUnit would store a different offset from the
      // one the designer dropped it at, by the snap-close error.
      labelDrag.last = { offsetAlongWall: t / pxPerRunUnit };
      return;
    }

    if (pinch && activePointers.size === 2) {
      const { dist: d, mid } = pinchMetrics();
      const factor = d / (pinch.startDist || 1);
      const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, pinch.startView.scale * factor));
      const startMid = toSvgPoint(pinch.startMid.x, pinch.startMid.y);
      const curMid = toSvgPoint(mid.x, mid.y);
      const anchorX = (startMid.x - pinch.startView.x) / pinch.startView.scale;
      const anchorY = (startMid.y - pinch.startView.y) / pinch.startView.scale;
      view = { scale: newScale, x: curMid.x - anchorX * newScale, y: curMid.y - anchorY * newScale };
      applyView();
      return;
    }

    if (panDrag) {
      const cur = toSvgPoint(evt.clientX, evt.clientY);
      view = panBy(view, cur.x - panDrag.last.x, cur.y - panDrag.last.y);
      panDrag.last = cur;
      applyView();
      return;
    }

    if (erasing) {
      eraseAt(evt.clientX, evt.clientY);
      return;
    }

    if (drawing) {
      const p = toModelPoint(evt.clientX, evt.clientY);
      if (drawing.straight) {
        // Rubber-band: a straight line from the anchor to the cursor.
        drawing.points = [drawing.anchor, p];
        activeStroke.setAttribute('points', pointsAttr(drawing.points));
        return;
      }
      const last = drawing.points[drawing.points.length - 1];
      // Annotations track finer than wall strokes, for a smoother pen line.
      const minStep = drawing.annotation ? 1.5 / view.scale : 3;
      if (distance(p, last) > minStep) {
        drawing.points.push(p);
        activeStroke.setAttribute('points', pointsAttr(drawing.points));
      }
    }
  });

  function endPointer(evt) {
    activePointers.delete(evt.pointerId);
    if (activePointers.size < 2) pinch = null;

    if (activeMeasure) {
      // Keep real drags; a stray tap (no length) leaves nothing behind.
      if (Math.hypot(activeMeasure.b.x - activeMeasure.a.x, activeMeasure.b.y - activeMeasure.a.y) > 3) {
        measures.push(activeMeasure);
      }
      activeMeasure = null;
      renderMeasures();
      try { svg.releasePointerCapture(evt.pointerId); } catch { /* not captured */ }
      return;
    }

    if (textDrag) {
      if (textDrag.last) handlers.onMoveText?.(textDrag.id, textDrag.last);
      textDrag = null;
      try { svg.releasePointerCapture(evt.pointerId); } catch { /* not captured */ }
      return;
    }

    if (sectionDrag) {
      if (sectionDrag.slot !== null) handlers.onSectionReorder?.(sectionDrag.id, sectionDrag.wallId, sectionDrag.slot);
      sectionDrag = null;
      try { svg.releasePointerCapture(evt.pointerId); } catch { /* not captured */ }
      return;
    }

    if (labelDrag) {
      if (labelDrag.last) handlers.onMoveLabel?.(labelDrag.id, labelDrag.last);
      labelDrag = null;
      try { svg.releasePointerCapture(evt.pointerId); } catch { /* not captured */ }
      return;
    }

    if (drawing) {
      const { points, annotation } = drawing;
      drawing = null;
      activeStroke.setAttribute('points', '');
      activeStroke.removeAttribute('stroke');
      activeStroke.removeAttribute('stroke-width');
      if (annotation) {
        if (points.length >= 2) handlers.onAnnotate?.(points, { ...annotationStyle });
      } else if (points.length > 2) {
        handlers.onStrokeEnd?.(points);
      }
    }
    erasing = false;
    panDrag = null;
    try { svg.releasePointerCapture(evt.pointerId); } catch { /* not captured */ }
  }

  svg.addEventListener('pointerup', endPointer);
  svg.addEventListener('pointercancel', endPointer);

  svg.addEventListener(
    'wheel',
    (evt) => {
      evt.preventDefault();
      zoomBy(evt.deltaY < 0 ? 1.12 : 1 / 1.12, evt.clientX, evt.clientY);
    },
    { passive: false },
  );

  setMode(mode);

  return {
    renderRaw,
    renderWalls,
    renderAnnotations,
    setAnnotationStyle,
    setAnnotationStraight,
    setMode,
    getMode: () => mode,
    fit,
    zoomIn: () => zoomBy(1.2),
    zoomOut: () => zoomBy(1 / 1.2),
    resetView: () => { view = identityView(); applyView(); },
    getView: () => ({ ...view }),
  };
}
